<template>
    <div class="blog-cta-wrapper">
        <div class="content">
            <SaleTag
                v-if="campaignText"
                class="sale-tag"
                :text="campaignText"
            />
            <h4>{{ content.title }}</h4>
            <div class="price">
                <h5>
                    {{ content.currentPrice }}
                </h5>
                <s
                    v-if="content.showStrikethroughPrice"
                    class="original-price"
                    >{{ content.originalPrice }}</s
                >
            </div>
            <div class="bulletpoints">
                <div
                    v-for="bulletpoint in content.bulletpoints"
                    class="bullet-wrapper"
                >
                    <TickRoundIcon />
                    <p class="bulletpoint">{{ bulletpoint }}</p>
                </div>
            </div>
            <p class="description">
                {{ content.description }}
            </p>
            <button class="btn-primary-medium button" @click="handleClick">
                {{ content.buttonTitle }}
            </button>
        </div>
        <img
            :src="image?.url"
            :alt="image?.imageMeta?.metaAltDescription"
            :title="image?.imageMeta?.metaTitle"
            class="blog-cta-image"
            loading="lazy"
        />
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useTranslation } from 'i18next-vue';
import SaleTag from '../SaleTag.vue';
import TickRoundIcon from '@/assets/icons/TickRoundIcon.vue';
import { useVendorAction } from '@/composables/usePurchaseFlow';
import { SHOPIFY_BUNDLE_COUNTRIES } from '@/utils/constants';
import { facebookGoogleAnalitycs } from '@/utils/helpers';

const store = useStore();
const { t } = useTranslation(['page-blog', 'shared']);

const { handleVendorAction } = useVendorAction();

const isMobile = computed(() => store.state.device.isMobile);
const countryCode = computed(() => store.state.prices?.prices?.countryCode);
const prices = computed(() => store.state.prices?.prices);
const campaignText = computed(() => store.state.campaign?.tags?.headerScanners);

const isShopifyCountry = computed(() =>
    SHOPIFY_BUNDLE_COUNTRIES.includes(countryCode.value)
);

const content = computed(() => {
    const campaign = store.state.campaign;

    const tag = campaign.tags?.headerScanners;

    const currency = prices.value?.currency;

    const discountedBundlePrice = campaign.prices?.bundle;
    const discountedEvoPrice = campaign.prices?.evo;

    const originalBundlePrice = prices.value?.bundlePrice;
    const originalEvoPrice = prices.value?.evoAdapterPrice;

    if (isShopifyCountry.value) {
        const bulletpoints = [
            t('page-blog_evo-scanner-plus-free-shipping'),
            t('page-blog_12-month-app-access-plus-updates'),
        ];

        return {
            tag,
            originalPrice: `${currency}${originalBundlePrice}`,
            currentPrice: `${currency}${
                discountedBundlePrice ?? originalBundlePrice
            }`,
            showStrikethroughPrice: Boolean(discountedBundlePrice),
            title: t('page-blog_best-deal-evo-and-app-pro'),
            buttonTitle: t('page-blog_claim-your-deal'),
            description: t('page-blog_join-carista-and-save-big'),
            bulletpoints,
        };
    }

    const bulletpoints = [
        t('page-blog_trusted-by-2m-drivers'),
        t('page-blog_vw-group-official-partner'),
        t('page-blog_200-plus-hidden-features'),
        t('page-blog_free-shipping-and-2-year-warranty'),
    ];

    return {
        tag,
        originalPrice: `${currency}${originalEvoPrice}`,
        currentPrice: `${currency}${discountedEvoPrice ?? originalEvoPrice}`,
        showStrikethroughPrice: Boolean(discountedEvoPrice),
        title: t('page-blog_unlock-more-with-evo'),
        buttonTitle: 'Get now',
        description: t('page-blog_works-best-with-app-subscription'),
        bulletpoints,
    };
});

const handleClick = () => {
    const event = isShopifyCountry.value ? 'claim_your_deal' : 'get_now_blog';

    facebookGoogleAnalitycs(event);
    handleVendorAction();
};

const getImageTag = () => {
    if (isShopifyCountry.value) {
        return 'blog-call-to-action-shopify-countries';
    }

    if (isMobile.value) {
        return 'blog-call-to-action-rest-of-countries-mobile';
    }

    return 'blog-call-to-action-rest-of-countries';
};

const image = computed(() => {
    let imageTag = getImageTag();

    const image = store.state.pages.homepage?.images?.find(
        (item) => item.tag == imageTag
    );

    return image;
});
</script>

<style lang="scss" scoped>
.blog-cta-wrapper {
    display: flex;
    align-items: end;
    gap: 30px;
    background-color: $color-light;
    padding: 30px 36px 0;
    border-radius: 24px;
    margin-bottom: 30px;

    .sale-tag {
        margin-bottom: 12px;
    }

    h4 {
        margin: 0px 0px 12px;
        color: $color-neutral-deep;

        font-size: 32px;
        font-weight: 500;
        letter-spacing: -0.32px;
    }

    .price {
        display: flex;
        align-items: center;
        gap: 10px;

        h5 {
            color: $color-neutral-deep;
            font-size: 24px;
            font-weight: 500;
        }

        s {
            color: $color-gray;
            font-size: 18px;
        }
    }

    .bulletpoints {
        display: flex;
        flex-direction: column;
        gap: 18px;
        margin: 24px 0;

        .bullet-wrapper {
            display: flex;
            gap: 8px;

            p {
                font-size: 16px !important;
                font-weight: 500 !important;
                margin: 0 !important;
            }
        }
    }

    .description {
        font-size: 16px !important;
        font-weight: 500 !important;
    }

    .btn-primary-medium {
        margin-bottom: 30px;
    }

    .blog-cta-image {
        object-fit: contain;
        height: fit-content;
        width: 43%;
    }

    @media (max-width: $screen-md) {
        flex-direction: column;
        max-width: 500px;
        padding: 18px 18px 0px 18px;
        gap: 0px;
        margin: 0 auto 30px;

        .bulletpoints {
            margin-bottom: 18px;
        }

        .blog-cta-image {
            display: block;
            padding: 0px 8px;
            margin: 0 !important;
        }

        .button {
            max-width: none;
        }
    }
}
</style>
